import React from 'react'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { NameDto } from 'api/Common/Types'
import { BusinessRuleDto } from 'api/Trading-Accounts/Types/BusinessRuleDto'
import { Loading } from 'components/Loading'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { FormSubmitValues } from 'utils/formValidation'
import { v4 as uuid } from 'uuid'

import { BusinessRule } from './CampaignDetailsBusinessRulesTable'

type CampaignDetailsBusinessRuleProps = FormikProps<CampaignDetailsBusinessRuleFormValues> &
  OuterProps

export interface CampaignDetailsBusinessRuleFormValues {
  businessRules: BusinessRuleDto[]
  businessRuleTypeId?: number
  businessRuleId?: number
  value?: number
}

const useCampaignDetailsBusinessRuleFetch = (props: CampaignDetailsBusinessRuleProps) => {
  const { values, setFieldValue } = props
  const { businessRuleTypeId } = values

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const { callback } = useCallbackWithForceRefresh(async () => {
    if (businessRuleTypeId) {
      const result =
        await tradingAccountsApiClient.getBusinessRuleTypesByRuleTypeId(businessRuleTypeId)
      setFieldValue('businessRules', result)
      return result
    }
    return Promise.resolve([])
  }, [businessRuleTypeId])
  const { data: businessRules = [], isLoading } = useFetchOne(callback)

  return { businessRules, isLoading }
}

export const CampaignDetailsBusinessRuleModalUI: React.FC<CampaignDetailsBusinessRuleProps> = (
  props
) => {
  const { campaignBusinessRuleTypes, setFieldValue } = props

  const { businessRules, isLoading } = useCampaignDetailsBusinessRuleFetch(props)

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Business Rule</DialogTitle>
      </DialogHeader>

      <Form>
        <Loading isLoading={isLoading}>
          <FormSelectField
            name='businessRuleTypeId'
            label='Business Rule Type'
            allowClear
            options={campaignBusinessRuleTypes}
            onChange={(value) => {
              setFieldValue('businessRuleTypeId', value ? Number(value) : undefined)
              setFieldValue('businessRuleId', undefined)
            }}
          />
          <FormSelectField
            name='businessRuleId'
            label='Business Rule'
            allowClear
            options={businessRules.map((rule) => ({
              id: rule.id,
              name: rule.name ?? '',
            }))}
            onChange={(value) => {
              setFieldValue('businessRuleId', value ? Number(value) : undefined)
            }}
          />
          <FormField name='value' label='Value' type='number' />

          <DialogFooter>
            <Button type='submit'>Save</Button>
          </DialogFooter>
        </Loading>
      </Form>
    </DialogContent>
  )
}

interface OuterProps {
  data?: BusinessRule
  businessRules: BusinessRule[]
  campaignBusinessRuleTypes: NameDto[]
  onSubmit(values?: FormSubmitValues<BusinessRule>): void
}

const dataToSubmit = (
  props: OuterProps,
  values: CampaignDetailsBusinessRuleFormValues
): FormSubmitValues<BusinessRule | undefined> => {
  const { data, campaignBusinessRuleTypes } = props
  const { businessRules, value, businessRuleId, businessRuleTypeId } = values

  const campaignBusinessRuleType = campaignBusinessRuleTypes.find(
    (x) => x.id === businessRuleTypeId
  )
  const campaignBusinessRule = businessRules.find((x) => x.id === businessRuleId)

  if (campaignBusinessRuleType && campaignBusinessRule) {
    return {
      id: data?.id || uuid(),
      type: { id: campaignBusinessRuleType.id, name: campaignBusinessRuleType.name || null },
      rule: { id: campaignBusinessRule.id, name: campaignBusinessRule.name || null },
      value: value || null,
    }
  }

  return data
}

const initialState = (props: OuterProps) => {
  const { data } = props
  return {
    businessRuleTypeId: data?.type?.id || undefined,
    businessRuleId: data?.rule?.id || undefined,
    value: data?.value || undefined,
    businessRules: [],
  }
}

export const CampaignDetailsBusinessRuleModal = withFormik<
  OuterProps,
  CampaignDetailsBusinessRuleFormValues
>({
  mapPropsToValues: (props) => {
    return initialState(props)
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(props, values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<CampaignDetailsBusinessRuleFormValues> = {}

    if (!values.businessRuleTypeId) {
      errors.businessRuleTypeId = 'Required'
    }

    if (!values.businessRuleId) {
      errors.businessRuleId = 'Required'
    }

    if (values.value && !Number.parseInt(values.value.toString(), 10)) {
      errors.value = 'Required'
    }

    return errors
  },
  enableReinitialize: true,
})(CampaignDetailsBusinessRuleModalUI)
